<template>
  <div class="section-spacing-20">
    <WarningMessage v-if="isDefault">
      <span>The generic equipment type cannot be edited.</span>
    </WarningMessage>
    <div class="section-spacing-10">
      <text-field
        v-model="formData.name"
        name="name"
        label="*Equipment Type Name"
        :validation-errors="v$.$errors"
        labelClass="full-width"
        :disabled="isDefault"
      />
      <text-area
        v-model="formData.description"
        name="Description"
        class="full-width"
        :disabled="isDefault"
      />
    </div>
    <div class="components">
      <h5 class="mb-2">{{ isEditing ? "Components" : "Add Components" }}</h5>
      <Select
        v-model="formData.component_type_ids"
        data-cy="components"
        name="components"
        label="*Components"
        :field-error="firstErrorMessage(v$.formData?.component_type_ids)"
        placeholder="Search and Select Components"
        :options="componentTypeOptions"
        class="full-width"
        full-width
        search
        multiple
        :disabled="isDefault"
        :no-label="isEditing"
      >
        <template #item-row="{ option }">
          <div class="option-row">
            <truncate-text
              class="ml-1"
              :class="{ 'strike-through': option.archived }"
              :text="option.label"
            />
          </div>
        </template>
      </Select>
    </div>
  </div>
</template>

<script>
import sortBy from "lodash/sortBy";
import WarningMessage from "serviceshift-ui/components/General/WarningMessage.vue";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import TextArea from "@/components/TextArea.vue";
import TextField from "@/components/TextField.vue";
import TruncateText from "@/components/TruncateText.vue";
import EquipmentType from "@/EquipmentTypes/EquipmentType.js";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

export default {
  components: {
    TextArea,
    TextField,
    TruncateText,
    WarningMessage
  },
  props: {
    componentTypes: {
      type: Array,
      default: () => []
    },
    equipmentType: {
      type: EquipmentType,
      default: null
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      formData: {
        name: "",
        description: "",
        component_type_ids: null // null | number[]
      }
    };
  },
  computed: {
    componentTypeOptions() {
      const componentTypes = this.componentTypes.filter((componentType) => {
        if (this.equipmentType) {
          return (
            this.equipmentType.component_type_ids.includes(componentType.id) ||
            !componentType.archived
          );
        }
        return !componentType.archived;
      });
      return sortBy(
        componentTypes.map((componentType) => {
          let label = componentType.name;
          if (componentType.description) {
            label += ` - ${componentType.description}`;
          }
          return {
            label,
            value: componentType.id,
            archived: componentType.archived
          };
        }),
        (componentType) => (componentType.label || "").toLowerCase()
      );
    },
    isEditing() {
      return !!this.equipmentType;
    },
    isDefault() {
      if (this.equipmentType && this.equipmentType.is_default) {
        return true;
      }

      return false;
    }
  },
  created() {
    this.setFormData();
  },
  validations() {
    return {
      formData: {
        name: { required, $autoDirty: true },
        component_type_ids: { required, $autoDirty: true }
      }
    };
  },
  methods: {
    async getData() {
      const isValid = await this.v$.$validate();
      if (isValid) {
        return this.formData;
      }
      return null;
    },
    setFormData() {
      if (this.equipmentType) {
        this.formData = { ...this.equipmentType };
      } else {
        this.formData = {
          name: "",
          description: "",
          component_type_ids: null
        };
      }
    },
    firstErrorMessage
  }
};
</script>

<style lang="scss" scoped>
.components {
  .select-box.disabled .erase-pill-icon {
    display: none;
  }
}
</style>
